<script lang="ts">
export const elIconButtonSizes = ['xs', 'sm', 'base', 'l', 'xl'] as const;
export type ElIconButtonSize = (typeof elIconButtonSizes)[number];
</script>

<script lang="ts" setup>
import ElIcon, { ElIconProps } from '@/ElIcon.vue';
import { computed } from 'vue';
import ElBadge, { ElBadgeColors } from '@/ElBadge.vue';
import ElTooltip from '@/ElTooltip.vue';

export interface ElIconButtonProps {
  icon: ElIconProps;
  tooltip?: string;
  tooltipDelay?: string | number;
  disabled?: boolean;
  size?: ElIconButtonSize;
  loading?: boolean;
  badge?: string | boolean;
  badgeColor?: ElBadgeColors;
  error?: boolean;
  showTooltipOnMobile?: boolean;
  hideTooltipOnChange?: boolean;
}

const props = withDefaults(defineProps<ElIconButtonProps>(), {
  tooltip: undefined,
  tooltipDelay: 600,
  size: 'base',
  badgeColor: 'primary',
  disabled: false,
  loading: false,
  badge: false,
  error: false,
  showTooltipOnMobile: false,
  hideTooltipOnChange: false,
});

const emit = defineEmits<{
  (event: 'click'): void;
}>();

const showBadge = computed(() => props.badge !== false && !props.error && !props.disabled && !props.loading);
const disabled = computed(() => props.disabled || props.loading || false);
</script>

<template>
  <ElTooltip
    :title="tooltip ?? ''"
    position="top-right"
    :delay-time="props.tooltipDelay"
    :hide-on-mobile="!showTooltipOnMobile"
    :hide-on-change="!!hideTooltipOnChange"
  >
    <button
      class="relative flex items-center justify-center outline-none"
      :class="{
        'h-6 w-6 rounded': size === 'xs',
        'h-7 w-7 rounded': size === 'sm',
        'h-8 w-8 rounded-md': size === 'base',
        'h-10 w-10 rounded-md': size === 'l',
        'h-12 w-12 rounded-md': size === 'xl',
        'cursor-not-allowed': disabled,
        'text-neutral-inactive': disabled,
        'hover:bg-error-light-hover focus:bg-error-light-pressed text-error bg-transparent': !disabled && error,
        'text-primary hover:text-primary-hover active:text-primary-pressed hover:bg-primary-light-hover active:bg-primary-light-pressed':
          !disabled && !error,
      }"
      type="button"
      :data-cy="tooltip ? `${tooltip.trim().replaceAll(' ', '')}-icon-button` : 'icon-button'"
      @click="disabled ? undefined : emit('click')"
    >
      <span v-if="loading" class="inline-flex items-center justify-center">
        <svg
          class="animate-spin"
          :class="{
            'h-4 w-4': size === 'xs',
            'h-5 w-5': size === 'sm',
            'h-6 w-6': size === 'base',
            'h-8 w-8': size === 'l',
            'h-10 w-10': size === 'xl',
          }"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </span>

      <ElIcon
        v-else
        :disabled="disabled"
        :name="icon.name"
        :solid="icon.solid"
        class="transition duration-150 ease-in-out"
        :class="{
          'h-4 w-4': size === 'xs',
          'h-5 w-5': size === 'sm',
          'h-6 w-6': size === 'base',
          'h-8 w-8': size === 'l',
          'h-10 w-10': size === 'xl',
        }"
      />
      <ElBadge
        v-if="showBadge"
        :color="props.badgeColor"
        :value="typeof badge === 'string' ? badge : undefined"
        :class="{
          'top-[2px] right-[1px]': size === 'xs',
          'top-[2px] right-[2px]': size === 'sm',
          'top-[4px] right-[4px]': size === 'base',
          'top-[5px] right-[5px]': size === 'l',
          'top-[6px] right-[6px]': size === 'xl',
        }"
      />
    </button>
  </ElTooltip>
</template>
